'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { buildRoute, DataTestConfig, WISHLIST_MODAL_ID } from '@headless-workspace/config';
import { useModalActions } from '@headless-workspace/core-ui';
import {
    GlowButton,
    GlowClickable,
    GlowIcon,
    GlowTextBody,
    GlowTitle,
    Icons,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { CommonDI, UserCookiesRepositoryType } from '../../../../DI';
import { useNavigation } from '../../../../client';

type WishlistModalProps = PropsWithLocale & {
    // Used to add or remove a product from the wishlist. Can be a sku or a PRODUCT_SET id.
    productId: string;
};

export const WishlistModal: Component<WishlistModalProps> = ({ productId, locale }) => {
    const t = useTranslations('Modal.wishlist');

    const navigation = useNavigation();
    const pathname = usePathname();

    const [isRedirecting, setIsRedirecting] = useState(false);
    const { close: closeModal } = useModalActions();

    const onConnectionClick = useCallback(async () => {
        const hostUrl = `${window.location.protocol}//${window.location.hostname}`;

        setIsRedirecting(true);

        await CommonDI.get(UserCookiesRepositoryType).createGuestProductIdCookie(locale, productId);

        navigation.push(
            buildRoute(locale, 'connection', {
                original: `${hostUrl}${buildRoute(locale, 'wishlist')}`,
            }),
        );
    }, [locale, productId, navigation]);

    const onClose = useCallback(() => {
        closeModal(WISHLIST_MODAL_ID);
    }, [closeModal]);

    useEffect(() => {
        setIsRedirecting(false);
    }, [pathname]);

    return (
        <div
            className={
                'flex flex-col items-center justify-center gap-1.5 w-wishlistModalMobile tablet:w-wishlistModal p-1 bg-background-l0 rounded-0.5'
            }
            data-testid={DataTestConfig.testIds.wishlistLoginModal}
        >
            <div className={'flex flex-row gap-x-1 w-full'}>
                <GlowTitle text={t('label.title')} fontSize={'title4'} TagName={'h2'} className={'flex-1'} />
                <GlowClickable content={{ icon: <GlowIcon Icon={Icons.Cross} type={'large'} /> }} onClick={onClose} />
            </div>
            <GlowTextBody text={t('label.description')} />

            <GlowButton
                label={t('action.login')}
                variant={'primary'}
                size={'large'}
                onClick={onConnectionClick}
                className={'w-full'}
                disabled={isRedirecting}
                isSpinnerActive={isRedirecting}
                dataTestId={DataTestConfig.testIds.wishlistLoginModalConnectionButton}
            />
        </div>
    );
};
